import type { Client, CreditPackage, PromotionDetails } from '@frontend/api-types';
import { Amex, Mastercard, Visa } from '@frontend/assets';
import { P, applyDiscount, formatCurrencyNatural, makeCent, match, pluralize } from '@frontend/duck-tape';
import type { MarketedMembershipPlan } from '../client/utils';

export type PaymentCardBrand = 'amex' | 'mastercard' | 'visa';
export const paymentCardBrands = { amex: Amex, mastercard: Mastercard, visa: Visa } as const satisfies Record<
  PaymentCardBrand,
  typeof Amex
>;

export const resolvePaymentCardBrand = (
  brand: string,
  { cardBrands = paymentCardBrands, fallback }: { cardBrands?: typeof paymentCardBrands; fallback: string },
): string => {
  return match(brand)
    .with(P.union('amex', 'mastercard', 'visa'), (b) => cardBrands[b as PaymentCardBrand])
    .otherwise(() => fallback);
};

export const formatSubscriptionEnd = (date: string) => new Date(date).toLocaleDateString();

export const buildCancellationURLParams = (client: Client) => {
  const params = [
    client?.firstName && client?.lastName ? `name=${client.firstName}+${client.lastName}` : undefined,
    client?.id ? `client_id=${client.id}` : undefined,
    client?.email ? `email=${encodeURIComponent(client.email)}` : undefined,
    client?.phoneNumber ? `phone_number=${encodeURIComponent(client.phoneNumber)}` : undefined,
    client?.price ? `price=${formatCurrencyNatural(client.price)}` : undefined,
    client?.subscriptionEnd ? `subscription_end=${formatSubscriptionEnd(client.subscriptionEnd)}` : undefined,
  ];
  return params.filter(Boolean).join('&');
};

export const formatPromoDetails = (promotionDetails: PromotionDetails): string => {
  const {
    amountOff: amountOffCents,
    durationInMonths,
    fixedPriceCents,
    freeTasksCap,
    isCreateSubscriptionOnSignup,
    percentOff,
  } = promotionDetails;

  const amountOff = amountOffCents ? formatCurrencyNatural(amountOffCents) : amountOffCents;
  const fixedPrice = fixedPriceCents ? formatCurrencyNatural(fixedPriceCents) : fixedPriceCents;
  // Treat freeTasksCap = 0 as if it is null
  const effectiveFreeTasksCap = freeTasksCap && freeTasksCap > 0 ? freeTasksCap : null;
  const afterText = isCreateSubscriptionOnSignup ? "You'll also" : "After that you'll";

  // Handle fixed-price promotions first
  if (fixedPriceCents !== null && fixedPriceCents !== undefined) {
    return match({ ...promotionDetails, freeTasksCap: effectiveFreeTasksCap })
      .with(
        {
          fixedPriceCents: P.number,
          freeTasksCap: P.number,
        },
        () =>
          `With this promo code, you'll pay ${fixedPrice} to get started with ${effectiveFreeTasksCap} credits. After your credits are used, you'll be charged the regular monthly rate.`,
      )
      .with(
        {
          fixedPriceCents: P.number,
        },
        () =>
          `With this promo code, you'll pay ${fixedPrice} to get started with your subscription at a discounted rate.`,
      )
      .otherwise(() => 'Special pricing available with this promo code.');
  }

  return match({ ...promotionDetails, freeTasksCap: effectiveFreeTasksCap })
    .with(
      {
        amountOff: P.number,
        durationInMonths: P.number,
        freeTasksCap: P.number,
        percentOff: P.number,
      },
      () =>
        `With this promo code, you'll get ${effectiveFreeTasksCap} free credits. ${afterText} get ${percentOff}% off for ${durationInMonths} ${pluralize('month', durationInMonths!)} and save ${amountOff}.`,
    )
    .with(
      {
        amountOff: P.number,
        durationInMonths: P.number,
        percentOff: P.number,
      },
      () =>
        `With this promo code, you'll get ${percentOff}% off for ${durationInMonths} ${pluralize('month', durationInMonths!)} and save ${amountOff}.`,
    )
    .with(
      {
        durationInMonths: P.number,
        freeTasksCap: P.number,
        percentOff: P.number,
      },
      () =>
        `With this promo code, you'll get ${effectiveFreeTasksCap} free credits. ${afterText} get ${percentOff}% off for ${durationInMonths} ${pluralize('month', durationInMonths!)}.`,
    )
    .with(
      {
        amountOff: P.number,
        durationInMonths: P.number,
        freeTasksCap: P.number,
      },
      () =>
        `With this promo code, you'll get ${effectiveFreeTasksCap} free credits. ${afterText} save ${amountOff} for ${durationInMonths} ${pluralize('month', durationInMonths!)}.`,
    )
    .with(
      {
        freeTasksCap: P.number,
        percentOff: P.number,
      },
      () =>
        `With this promo code, you'll get ${effectiveFreeTasksCap} free credits. ${afterText} get ${percentOff}% off.`,
    )
    .with(
      {
        amountOff: P.number,
        freeTasksCap: P.number,
      },
      () => `With this promo code, you'll get ${effectiveFreeTasksCap} free credits. ${afterText} save ${amountOff}.`,
    )
    .with(
      {
        durationInMonths: P.number,
        percentOff: P.number,
      },
      () =>
        `With this promo code, you'll get ${percentOff}% off for ${durationInMonths} ${pluralize('month', durationInMonths!)}.`,
    )
    .with(
      {
        amountOff: P.number,
        durationInMonths: P.number,
      },
      () =>
        `With this promo code, you'll save ${amountOff} for ${durationInMonths} ${pluralize('month', durationInMonths!)}.`,
    )
    .with(
      {
        amountOff: P.number,
        percentOff: P.number,
      },
      () => `With this promo code, you'll get ${percentOff}% off and save ${amountOff}.`,
    )
    .with(
      {
        percentOff: P.number,
      },
      () => `With this promo code, you'll get ${percentOff}% off.`,
    )
    .with(
      {
        amountOff: P.number,
      },
      () => `With this promo code, you'll save ${amountOff}.`,
    )
    .with(
      {
        freeTasksCap: P.number,
      },
      () => `With this promo code, you'll get ${effectiveFreeTasksCap} free credits.`,
    )
    .otherwise(() => 'This promo code has no discount details.');
};

export const formatPromoPricingAffect = (
  basePrice: Cent,
  promotionDetails: PromotionDetails,
  simple?: boolean,
): string => {
  const { amountOff, durationInMonths, percentOff } = promotionDetails;
  const promo = { amountOff, durationInMonths, percentOff };
  const basePriceString = `${formatCurrencyNatural(basePrice)}/month`;

  if (!durationInMonths || durationInMonths <= 0) {
    return basePriceString;
  }

  const discountedPrice = match(promo)
    .with(
      {
        percentOff: P.number,
      },
      ({ percentOff }) => applyDiscount(basePrice, percentOff),
    )
    .with(
      {
        amountOff: P.number,
      },
      ({ amountOff }) => makeCent(Math.max(0, basePrice - amountOff)),
    )
    .otherwise(() => null);

  if (discountedPrice !== null) {
    const nonNegativeDiscountedPrice = makeCent(Math.max(0, discountedPrice));
    if (simple) {
      return `${formatCurrencyNatural(nonNegativeDiscountedPrice)}`;
    }
    return `${formatCurrencyNatural(nonNegativeDiscountedPrice)}/month for your first ${durationInMonths} ${pluralize('month', durationInMonths)}`;
  }

  return basePriceString;
};

export type FreeCreditsOnSignup = {
  days_until_expiration: number;
  message: string;
  num_credits: number;
  promotion_id_for_variant: string;
  variant_days_until_expiration: number;
  variant_message: string;
  variant_num_credits: number;
};

export type CreditDisplayData = {
  days_until_expiration: number;
  message: string;
  num_credits: number;
};

/**
 * Gets the appropriate credit display data based on promotion details.
 * Returns promotional variant data if the promotion ID matches, otherwise returns default data.
 *
 * @param freeCreditsOnSignup - The free credits configuration from feature flags
 * @param promoDetails - The current promotion details (if any)
 * @returns Credit display data or null if no credits available
 */
export const getCreditDisplayData = (
  freeCreditsOnSignup: FreeCreditsOnSignup | null | undefined,
  promoDetails: PromotionDetails | null | undefined,
): CreditDisplayData | null => {
  if (!freeCreditsOnSignup || freeCreditsOnSignup.num_credits <= 0) {
    return null;
  }

  // Check if we should show promotional variant
  if (promoDetails?.promotionId && freeCreditsOnSignup.promotion_id_for_variant === promoDetails.promotionId) {
    return {
      days_until_expiration: freeCreditsOnSignup.variant_days_until_expiration,
      message: freeCreditsOnSignup.variant_message,
      num_credits: freeCreditsOnSignup.variant_num_credits,
    };
  }

  // Return default data
  return {
    days_until_expiration: freeCreditsOnSignup.days_until_expiration,
    message: freeCreditsOnSignup.message,
    num_credits: freeCreditsOnSignup.num_credits,
  };
};

export type PurchaseType = 'ALaCarteCredits' | 'Subscription';

export type FirstPurchaseData = {
  selectedCreditsPackage?: CreditPackage;
  selectedPlanData?: MarketedMembershipPlan;
  selectedPurchaseType?: PurchaseType;
};

const getPriceWithPromotion = ({
  membershipPlan,
  promotionDetails: { amountOff, fixedPriceCents, percentOff },
}: {
  membershipPlan: MarketedMembershipPlan;
  promotionDetails: {
    amountOff: MaybeNull<Cent>;
    fixedPriceCents: MaybeNull<Cent>;
    percentOff: MaybeNull<number>;
  };
}): Cent =>
  match({ amountOff, fixedPriceCents, percentOff })
    .with({ fixedPriceCents: P.number }, ({ fixedPriceCents }) => fixedPriceCents as Cent)
    .with({ percentOff: P.number }, ({ percentOff }) => applyDiscount(membershipPlan.price, percentOff as number))
    .with({ amountOff: P.number }, ({ amountOff }) => makeCent(Math.max(0, membershipPlan.price - (amountOff as Cent))))
    .otherwise(() => membershipPlan.price);

/**
 * Calculates the amount due today given purchase selection and promotion details
 */
export const calculateAmountDueToday = ({
  firstPurchaseData,
  promotionDetails,
}: {
  firstPurchaseData?: FirstPurchaseData;
  promotionDetails?: PromotionDetails | null;
}): MaybeUndefined<Cent> =>
  match({
    firstPurchaseData,
    promotionDetails: promotionDetails
      ? {
          amountOff: promotionDetails.amountOff,
          countFreeCredits: promotionDetails.freeTasksCap,
          fixedPriceCents: promotionDetails.fixedPriceCents,
          isCreateSubscriptionOnSignup: promotionDetails.isCreateSubscriptionOnSignup,
          percentOff: promotionDetails.percentOff,
        }
      : null,
  })
    .with(
      {
        firstPurchaseData: {
          selectedPlanData: P.nonNullable.select('selectedPlanData'),
          selectedPurchaseType: 'Subscription',
        },
        promotionDetails: P.select('promotionDetails'),
      },
      ({ promotionDetails, selectedPlanData }) =>
        match(promotionDetails)
          .with(P.nullish, () => selectedPlanData.price)
          /** Fixed price takes precedence - user pays this amount regardless of other factors */
          .with({ fixedPriceCents: P.number }, ({ fixedPriceCents }) => makeCent(fixedPriceCents))
          /** No subscription until credits are finished - only when explicitly false */
          .with({ countFreeCredits: P.number, isCreateSubscriptionOnSignup: false }, () => makeCent(0))
          /** Subscription created immediately (true, null, or undefined) - apply promotion */
          .when(
            ({ isCreateSubscriptionOnSignup }) => isCreateSubscriptionOnSignup !== false,
            ({ amountOff, fixedPriceCents, percentOff }) =>
              getPriceWithPromotion({
                membershipPlan: selectedPlanData,
                promotionDetails: { amountOff, fixedPriceCents, percentOff },
              }),
          )
          .otherwise(() => selectedPlanData.price),
    )
    .with(
      {
        firstPurchaseData: {
          selectedCreditsPackage: P.nonNullable.select('selectedCreditsPackage'),
          selectedPurchaseType: 'ALaCarteCredits',
        },
      },
      ({ selectedCreditsPackage }) => selectedCreditsPackage.priceCent,
    )
    .otherwise(() => undefined);
